import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import {
  LayoutDashboard, FolderKanban, Users, Package,
  FileText, Settings, LogOut, ChevronLeft, ChevronRight,
  Printer, Layers, Warehouse, Truck, DollarSign, Wrench, Activity, History
} from 'lucide-react';

const NAV_ITEMS = [
  { to: '/',           icon: LayoutDashboard, label: 'Dashboard',   role: null,              exact: true },
  { to: '/gestor',     icon: FolderKanban,    label: 'Proyectos',   role: 'ROLE_GESTOR',     exact: false },
  { to: '/clientes',   icon: Users,           label: 'Clientes',    role: 'ROLE_GESTOR',     exact: false },
  { to: '/plantillas', icon: FileText,        label: 'Plantillas',  role: 'ROLE_GESTOR',     exact: false },
  { to: '/stock',         icon: Package,   label: 'Stock',            role: 'ROLE_ALMACEN',    exact: true },
  { to: '/stock/consumo', icon: History,   label: 'Consumo Mat.',     role: 'ROLE_ALMACEN',    exact: false },
  { to: '/financiero', icon: DollarSign,      label: 'Financiero',  role: 'ROLE_FINANCIERO', exact: false },
  { to: '/imprenta',   icon: Printer,         label: 'Imprenta',    role: 'ROLE_IMPRENTA',   exact: false },
  { to: '/dtf',        icon: Layers,          label: 'DTF',         role: 'ROLE_DTF',        exact: false },
  { to: '/maquinas',   icon: Wrench,          label: 'Máquinas',    role: 'ROLE_IMPRENTA',   exact: false, extraRole: 'ROLE_DTF' },
  { to: '/almacen',    icon: Warehouse,       label: 'Almacén',     role: 'ROLE_ALMACEN',    exact: false },
  { to: '/embalaje',   icon: Truck,           label: 'Embalaje',    role: 'ROLE_EMBALAJE',   exact: false },
  { to: '/usuarios',         icon: Settings,  label: 'Usuarios',         role: 'ROLE_ADMIN', exact: false },
  { to: '/admin/actividad',  icon: Activity,  label: 'Actividad',        role: 'ROLE_ADMIN', exact: false },
];

interface SidebarProps {
  collapsed?: boolean;
  onToggle?: () => void;
}

export function Sidebar({ collapsed = false, onToggle }: SidebarProps) {
  const { user, logout, hasRole } = useAuthStore();
  const navigate = useNavigate();

  const handleLogout = () => { logout(); navigate('/login'); };

  const visibleItems = NAV_ITEMS.filter(item =>
    !item.role || hasRole(item.role) || ('extraRole' in item && item.extraRole && hasRole(item.extraRole as string)) || hasRole('ROLE_ADMIN')
  );

  return (
    <aside style={{
      width: collapsed ? 64 : 220,
      minHeight: '100vh',
      background: '#111014',
      borderRight: '1px solid #27262f',
      display: 'flex',
      flexDirection: 'column',
      transition: 'width 0.3s cubic-bezier(0.16,1,0.3,1)',
      position: 'relative',
      flexShrink: 0,
    }}>
      {/* Logo */}
      <div style={{
        padding: collapsed ? '20px 0' : '20px 16px',
        borderBottom: '1px solid #27262f',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        overflow: 'hidden',
        justifyContent: collapsed ? 'center' : 'flex-start',
      }}>
        <div style={{
          width: 32, height: 32,
          background: 'linear-gradient(135deg, #f97316, #ea580c)',
          borderRadius: 8,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
          fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 14, color: '#fff',
          boxShadow: '0 0 16px rgba(249,115,22,0.3)',
        }}>P</div>
        {!collapsed && (
          <div>
            <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 14, color: '#f4f3f8', letterSpacing: '-0.02em' }}>PRINT</div>
            <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 9, color: '#4a4858', letterSpacing: '0.1em' }}>ERP v1.0</div>
          </div>
        )}
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, padding: '12px 0', overflowY: 'auto' }}>
        {visibleItems.map(({ to, icon: Icon, label, exact }) => (
          <NavLink
            key={to}
            to={to}
            end={exact}
            style={({ isActive }) => ({
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '10px 16px',
              color: isActive ? '#f97316' : '#6b6880',
              background: isActive ? 'rgba(249,115,22,0.08)' : 'transparent',
              borderLeft: isActive ? '2px solid #f97316' : '2px solid transparent',
              textDecoration: 'none', fontSize: 13, fontFamily: 'DM Sans, sans-serif',
              fontWeight: isActive ? 600 : 400, transition: 'all 0.15s', overflow: 'hidden',
              whiteSpace: 'nowrap',
              justifyContent: collapsed ? 'center' : 'flex-start',
            })}
            title={collapsed ? label : undefined}
          >
            <Icon size={16} style={{ flexShrink: 0 }} />
            {!collapsed && label}
          </NavLink>
        ))}
      </nav>

      {/* User */}
      {user && (
        <div style={{ borderTop: '1px solid #27262f', padding: collapsed ? '12px 8px' : '12px 16px' }}>
          {!collapsed && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              {user.avatar
                ? <img src={user.avatar} alt="" style={{ width: 28, height: 28, borderRadius: '50%', border: '1px solid #33323d' }} />
                : <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#27262f', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: '#f97316', fontWeight: 700 }}>{user.name[0]}</div>
              }
              <div style={{ overflow: 'hidden' }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#c4c2d4', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.name}</div>
                <div style={{ fontSize: 10, color: '#4a4858', fontFamily: 'JetBrains Mono, monospace', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.email}</div>
              </div>
            </div>
          )}
          <button
            onClick={handleLogout}
            title={collapsed ? 'Cerrar sesión' : undefined}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              color: '#4a4858', background: 'none', border: 'none',
              cursor: 'pointer', fontSize: 12, padding: '4px 0', width: '100%',
              justifyContent: collapsed ? 'center' : 'flex-start',
            }}
          >
            <LogOut size={14} />
            {!collapsed && 'Cerrar sesión'}
          </button>
        </div>
      )}

      {/* Toggle */}
      <button
        onClick={onToggle}
        style={{
          position: 'absolute', top: 18, right: -12,
          width: 24, height: 24, borderRadius: '50%',
          background: '#1c1b22', border: '1px solid #33323d',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', color: '#6b6880', zIndex: 10,
        }}
      >
        {collapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
      </button>
    </aside>
  );
}
